import { Box, Flex } from '@src/components/Core';
import { Tooltip } from '@src/components/Tooltip';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import type { AgGridContext } from 'ag-grid-community/dist/types/core/interfaces/iCommon';
import { get } from 'lodash-es';
import { Text } from '../../Text';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

/**
 * The StringArrayColumnValue type contains both the "real" value in the type of a sorted markets array, but also
 * includes the effective formatted value. This is because we want to do sorting (comparator callback) on the formatted value.
 *
 * The comparator callback does not have access to the formatted value, only the value.
 */
export type StringArrayColumnValue = { arr: string[]; str: string };

export interface StringArrayColumnParams {
  getDisplayName: (value: string, context: AgGridContext) => string;
  /** If the array is nullish or empty, provide a text to show in its place. The text will be grayed out and italicized. */
  emptyArrayText?: string;
  /** Defaults to true. Whether or not to sort the resolved array by the display name */
  sortByDisplayName?: boolean;
}

/**
 * The string array is a generic column factory for the case where you have an array of strings you want to represent in a  blotter column.
 *
 * If you just provide the column with how to resolve a display name for each string, the column will render a sorted csv-list
 * of display names. The column is also sortable (basic alphabetic sort on the concatenated rendered string).
 */
export const stringArray: ColDefFactory<Column<StringArrayColumnParams>> = column => ({
  ...baseColumn(column),
  valueGetter: ({ data, context }: ValueGetterParams<unknown>): StringArrayColumnValue => {
    if (!data || !column.field) {
      return { arr: [], str: '' };
    }

    const inputArray = get(data, column.field);
    if (!(inputArray instanceof Array)) {
      return { arr: [], str: '' };
    }

    let array = [...inputArray]; // shallow copy so we dont sort the input array reference below

    const sortByDisplayName = column.params?.sortByDisplayName ?? true; // default to true
    if (sortByDisplayName) {
      array = array.toSorted((a, b) => {
        if (typeof a !== 'string') {
          return 1;
        }

        if (typeof b !== 'string') {
          return -1;
        }

        const aName = column.params?.getDisplayName(a, context) ?? a;
        const bName = column.params?.getDisplayName(b, context) ?? b;
        return aName.localeCompare(bName);
      });
    }

    const sortedStr = array.map(str => column.params?.getDisplayName(str, context) ?? str).join(', ');

    return {
      arr: array,
      str: sortedStr,
    };
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, StringArrayColumnValue>): string => {
    if (isValueEmpty(value) && column.params?.emptyArrayText) {
      return column.params.emptyArrayText;
    }

    return value?.str ?? '';
  },
  // When you want to know how to filter a string array column, we only return the raw data (the array of strings)
  filterValueGetter: ({ data }: ValueGetterParams<unknown>): string[] => {
    if (!data || !column.field) {
      return [];
    }

    const arr = get(data, column.field);
    if (!(arr instanceof Array)) {
      return [];
    }

    return arr;
  },
  cellRenderer: ({ value, valueFormatted, context }: ICellRendererParams<unknown, StringArrayColumnValue>) => {
    if (value == null) {
      return null;
    }

    if (isValueEmpty(value) && column.params?.emptyArrayText) {
      return (
        <Text color="colorTextSubtle" fontStyle="italic">
          {column.params.emptyArrayText}
        </Text>
      );
    }

    return (
      <Box>
        <Tooltip
          usePortal
          placement="top-start"
          showUnderline
          tooltip={
            <>
              {value.arr.map(str => (
                <Flex alignItems="center" key={str} justifyContent="flex-start">
                  <span>{column.params?.getDisplayName(str, context) ?? str}</span>
                </Flex>
              ))}
            </>
          }
        >
          {valueFormatted}
        </Tooltip>
      </Box>
    );
  },
  comparator: (valueA: StringArrayColumnValue, valueB: StringArrayColumnValue) => {
    return valueA.str.localeCompare(valueB.str);
  },
});

function isValueEmpty(value: StringArrayColumnValue | null | undefined) {
  return value == null || value.arr.length === 0;
}
